import * as React from "react";
import { LogOut, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandSwitcher } from "@/components/chrome/BrandSwitcher";
import { useAuth } from "@/auth/useAuth";
import { useAppSelector } from "@/store";
import { selectProfile } from "@/store/selectors";

function computeInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return "BH";
  return parts.slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

export function DevChrome() {
  const profile = useAppSelector(selectProfile);
  const { logout } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const agencyName = profile.agencyName ?? "";
  const initials = computeInitials(agencyName);

  return (
    <header className="relative z-30 flex items-center justify-between px-6 py-5">
      <BrandSwitcher agencyName={agencyName} dark />
      <nav className="flex items-center gap-2">
        <a
          href="https://docs.hoboh.dev"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] tracking-tight text-white/60 transition-colors hover:bg-white/[0.04] hover:text-white"
        >
          Docs <ArrowUpRight className="h-3 w-3" strokeWidth={1.6} />
        </a>
        <div ref={ref} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className={cn(
              "grid h-8 w-8 place-items-center rounded-full text-[10px] font-medium uppercase tracking-wider transition-colors",
              menuOpen
                ? "bg-white text-[#0a0b14]"
                : "bg-white/[0.06] text-white/80 ring-1 ring-white/10 hover:bg-white/[0.10]",
            )}
            aria-label="Profilo"
          >
            {initials}
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 overflow-hidden rounded-[14px] bg-[#15161e]/95 backdrop-blur-2xl shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_24px_60px_-18px_rgba(0,0,0,0.6)] animate-riseIn">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] text-white/60 transition-colors hover:bg-white/[0.04] hover:text-white"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                Esci
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
